import "@event-calendar/core/index.css";
import React from "react";
import { renderToStaticMarkup } from "react-dom/server.browser";
import { useLoaderData } from "react-router";
import {
  createCalendar,
  ResourceTimeGrid,
  Interaction,
  destroyCalendar,
} from "@event-calendar/core";
import * as api from "../services/api";
import { Box, Button } from "@mui/material";
import DatePicker2 from "../components/mui/DatePicker";
import * as f from "../utils/formatter";
import * as ws from "../utils/ws";
import { useTranslation } from "react-i18next";
import * as calUtil from "shared/src/calendar.mjs";
import * as d from "shared/src/date.mjs";
import { throttle } from "shared/src/util.mjs";
import { THROTTLE_REFRESH } from "../constants";
import {
  appointmentColor,
  appointmentInfoColor,
  bufferService,
  createClickHandler,
  TagList,
  waitingAppointmentBackground,
} from "../components/calendar";
import { DatePicker } from "../components/DatePicker";
import {
  currentLocationFromRequest,
  useCurrentLocation,
} from "../components/LocationSelector";
import ServiceTag from "../components/ServiceTag.jsx";
import { presentStatuses, status } from "shared/src/appointment.mjs";
import { AppointmentDetailDialog } from "../components/calendar/AppointmentDetailDialog";
import { duration } from "../components/resource-list/fn.mjs";
import { role } from "shared/src/roles.mjs";
import { createUrlString } from "../url";
import { useRoles } from "../context/AuthenticationTokenContext";
import VirtualCareTag from "../components/VirtualCareTag.jsx";

const eventType = {
  APPOINTMENT: "appointment",
  BLOCATION: "blocation",
  BUFFER: "buffer",
};

/**
 * @typedef EventCalendar
 * @property {() => Event[]} getEvents
 * @property {(e: Event) => EventCalendar} updateEvent
 *
 * @typedef Appointment
 * @property {string} start
 * @property {string} end
 * @property {number} status_id
 * @property {string} calendar_id
 *
 * @typedef Calendar
 * @property {string} id
 * @property {string} title
 * @property {string} location_id
 *
 * @typedef Resource
 * @property {Calendar} extendedProps
 *
 * @typedef Event
 * @property {Date} start
 * @property {Date} end
 * @property {Appointment} extendedProps
 *
 * @typedef Buffer
 * @property {string} calendar_id
 * @property {string} from
 * @property {string} to
 */

export async function loader({ request }) {
  const location_id = currentLocationFromRequest(request);

  return api.loadCalendarData(
    {
      calendarFilter: {
        visible: { eq: true },
        location_id: { eq: location_id },
      },
      location_id,
      day: d.toDateString(new Date()),
    },
    api.contextFromRequest(request),
  );
}

function EventContent({ event, appointment, done }) {
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: "0.2rem" }}>
        <VirtualCareTag client={appointment.client} />
        <div
          style={{
            fontWeight: 700,
            minHeight: "1.5rem",
          }}
          className="single-line"
        >
          {f.fullName(appointment.client)}
        </div>
      </div>
      <ServiceTag service={appointment.service} disabled={done} />
      <div
        style={{
          color: done
            ? "var(--mui-palette-action-disabled)"
            : appointmentInfoColor(appointment),
          fontWeight: 700,
        }}
      >
        {f.time24(appointment.start)}-{f.time24(appointment.end)} (
        {f.minutesToDuration(duration(event.start, event.end))})
      </div>
    </>
  );
}

function BlocationContent({ blocation }) {
  return (
    <>
      <div style={{ fontWeight: 700 }}>{blocation.title}</div>
    </>
  );
}

function BufferContent() {
  return (
    <>
      <ServiceTag service={bufferService} />
    </>
  );
}

const doneStatuses = new Set([status.TREATED, status.FINISHED]);

function ReservationOverlay({ appointment }) {
  if (appointment.reservation_id == null) {
    return null;
  }

  return (
    <div
      className={`calendar-event--reservation-overlay calendar-event--reservation-overlay--${appointment.reservation_id} hidden`}
    ></div>
  );
}

function createEventContentFn() {
  return function (info) {
    switch (info.event.title) {
      case eventType.APPOINTMENT: {
        const appointment = info.event.extendedProps;
        const done = doneStatuses.has(appointment.status_id);
        const present = presentStatuses.has(appointment.status_id);

        return {
          html: renderToStaticMarkup(
            <div style={{ display: "flex", height: "100%" }}>
              <TagList appointments={[appointment]} disable={done} />
              <div
                className="calendar-event"
                style={{
                  background: present
                    ? waitingAppointmentBackground(appointment)
                    : "#ffffff",
                  borderColor: appointmentColor(appointment),
                  color: done
                    ? "var(--mui-palette-action-disabled)"
                    : "var(--mui-palette-_components-heading-color)",
                  padding: "var(--app-calendar-event-padding)",
                }}
              >
                <ReservationOverlay appointment={appointment} />
                <EventContent
                  event={info.event}
                  appointment={appointment}
                  done={done}
                />
              </div>
            </div>,
          ),
        };
      }
      case eventType.BLOCATION: {
        const blocation = info.event.extendedProps;

        return {
          html: renderToStaticMarkup(
            <div style={{ display: "flex", height: "100%" }}>
              <div
                className="calendar-event"
                style={{
                  background: "#fff",
                  borderColor: appointmentColor(blocation),
                  color: "var(--mui-palette-_components-heading-color)",
                  padding: "var(--app-calendar-event-padding)",
                }}
              >
                <BlocationContent blocation={blocation} />
              </div>
            </div>,
          ),
        };
      }
      case eventType.BUFFER: {
        return {
          html: renderToStaticMarkup(
            <div style={{ display: "flex", height: "100%" }}>
              <div
                className="calendar-event"
                style={{
                  background: "#fff",
                  borderColor: appointmentColor({ service: bufferService }),
                  color: "var(--mui-palette-_components-heading-color)",
                  padding: "var(--app-calendar-event-padding)",
                }}
              >
                <BufferContent />
              </div>
            </div>,
          ),
        };
      }
    }
  };
}

function setReservationOverlay(reservation_id) {
  document
    .querySelectorAll(`.calendar-event--reservation-overlay`)
    .forEach((el) => {
      el.classList.add("hidden");
    });

  if (reservation_id != null) {
    document
      .querySelectorAll(
        `.calendar-event--reservation-overlay--${reservation_id}`,
      )
      .forEach((el) => {
        el.classList.remove("hidden");
      });
  }
}

/**
 * @param {object} opts
 * @param {HTMLElement} opts.el
 * @param {React.RefObject<string | undefined>} opts.location_idRef
 * @param {React.RefObject<Buffer[]>} opts.buffers_ref
 * @param {(event: object | null) => void} opts.setEditEvent
 */
function createEcal({ el, setEditEvent, location_idRef, buffers_ref }) {
  const clickHandler = createClickHandler({
    onClick: (info) => {
      setEditEvent([info.event.extendedProps]);
    },
    onDoubleClick: (info) => {
      const clientId = info.event.extendedProps?.client?.id;
      if (clientId == null) {
        return;
      }

      window.open(
        createUrlString(
          location.origin,
          location.search,
          `/clients/${clientId}`,
        ),
        "_blank",
      );
    },
  });

  return createCalendar(el, [ResourceTimeGrid, Interaction], {
    view: "resourceTimeGridDay",
    nowIndicator: true,
    headerToolbar: { start: "", center: "", end: "" },
    slotLabelFormat: { timeStyle: "short", hour12: false },
    allDaySlot: false,
    flexibleSlotTimeLimits: true,
    scrollTime: "09:00",
    eventTextColor: "#000",
    eventDurationEditable: false,
    eventStartEditable: false,
    slotHeight: 160,
    eventClassNames: (info) =>
      `event-id-${info.event.id} status-${info.event.extendedProps.status_id}`,
    eventContent: createEventContentFn(),
    eventClick: (info) => {
      if (info.event.title !== eventType.APPOINTMENT) {
        return;
      }
      clickHandler(info);
    },
    eventMouseEnter: ({ event }) => {
      const reservation_id = event.extendedProps.reservation_id;
      setReservationOverlay(reservation_id);
    },
    eventMouseLeave: () => {
      setReservationOverlay(null);
    },
    eventAllUpdated: () => {
      const ecExtra = document.querySelector(".ec-now-indicator");
      ecExtra?.setAttribute("data-now-time", f.time24(new Date()));
    },
    eventSources: [
      {
        events: async function (fetchInfo) {
          const location_id = location_idRef.current;
          if (location_id == null) {
            return [];
          }

          const resp = await api.loadCalendarAppointments(
            {
              now: d.toString(new Date()),
              location_id,
              filter: {
                start: {
                  gte: d.toString(fetchInfo.start),
                  lt: d.toString(fetchInfo.end),
                },
                duration: {
                  neq: 0,
                },
                location_id: { eq: location_id },
                status_id: { neq: status.CANCELED },
                adhoc: { eq: false },
              },
              blocationFilter: {
                valid_from: {
                  lte: d.toDateString(fetchInfo.start),
                },
                valid_to: {
                  gte: d.toDateString(fetchInfo.end),
                },
                location_id: { eq: location_id },
                or: [
                  {
                    days: {
                      array_overlaps: [calUtil.dateToDay(fetchInfo.start)],
                    },
                  },
                  {
                    days: {
                      array_empty: true,
                    },
                  },
                ],
              },
            },
            api.contextFromLocation(location_id),
          );

          const todayDate = d.toDateString(fetchInfo.start);

          const blocations = resp.data.locationBlocations.data.flatMap(
            (blocation) => {
              return blocation.calendars.map((c) => {
                const event = {
                  title: eventType.BLOCATION,
                  start: new Date(`${todayDate} ${blocation.from}`),
                  end: new Date(`${todayDate} ${blocation.to}`),
                  resourceId: c.calendar_id,
                  extendedProps: blocation,
                };

                return event;
              });
            },
          );

          const appointments = resp.data.appointments.data.map((e) => {
            const event = {
              ...e,
              title: eventType.APPOINTMENT,
              resourceId: e.calendar_id,
              start: new Date(e.start),
              end: new Date(e.end),
              extendedProps: e,
            };

            return event;
          });

          const buffers = buffers_ref.current.map((b) => {
            const event = {
              title: eventType.BUFFER,
              start: new Date(b.from),
              end: new Date(b.to),
              resourceId: b.calendar_id,
              extendedProps: b,
            };

            return event;
          });

          return [...buffers, ...blocations, ...appointments];
        },
      },
    ],
  });
}

/**
 * @param {object} msg
 * @param {string} location_id
 * @param {string} date
 *
 * @returns {boolean}
 */
function isInterestingNotification(msg, location_id, date) {
  switch (msg.type) {
    case "__connect":
      return true;
    case "appointment_changed": {
      const { location_ids, days } = msg.payload;

      if (Object.keys(msg.payload).length === 0) {
        return true;
      }

      return location_ids?.includes(location_id) && days.includes(date);
    }
  }

  return false;
}

const Cal = React.memo(function Cal({
  date,
  setEditEvent,
  location_id,
  location,
  resources,
  buffers,
}) {
  const cal = React.useRef();
  const ecalEl = React.useRef();
  const location_idRef = React.useRef();
  const buffers_ref = React.useRef([]);

  React.useEffect(() => {
    const refresh = throttle(() => {
      cal.current?.refetchEvents();
    }, THROTTLE_REFRESH);

    function maybeRefresh(msg) {
      if (isInterestingNotification(msg, location_idRef.current, date)) {
        refresh();
      }
    }

    ws.subscribe(maybeRefresh);

    return () => {
      ws.unsubscribe(maybeRefresh);
    };
  }, [date]);

  React.useEffect(() => {
    location_idRef.current = location_id;
    buffers_ref.current = buffers;
    cal.current?.refetchEvents();
  }, [location_id, buffers]);

  React.useEffect(() => {
    cal.current = createEcal({
      el: ecalEl.current,
      setEditEvent,
      location_idRef,
      buffers_ref,
    });

    return function () {
      destroyCalendar(cal.current);
      cal.current = null;
    };
  }, []);

  React.useEffect(() => {
    if (cal.current == null) {
      return;
    }

    cal.current.setOption("resources", resources);
  }, [resources]);

  React.useEffect(() => {
    const c = cal.current;
    if (c == null) {
      return;
    }

    const d = new Date(date);
    const day = calUtil.dateToDay(d);

    const wh = calUtil.workingHoursInDay(d, location?.working_hours?.[day]);
    const slotMinTime = wh?.from ?? "00:00";
    const slotMaxTime = wh?.to ?? "24:00";

    c.setOption("date", date);
    c.setOption("slotMinTime", slotMinTime);
    c.setOption("slotMaxTime", slotMaxTime);
  }, [date, resources]);

  return <div id="ecal" ref={ecalEl} />;
});

const Toolbar = React.memo(function Toolbar({ setState, date, disabled }) {
  const { t } = useTranslation();
  const setShowYearDialog = (showYearDialog) =>
    setState((state) => ({ ...state, showYearDialog }));

  return (
    <Box display="flex" gap={2} marginBottom={2}>
      <DatePicker2
        label={t("calendar.dateLabel")}
        onChange={(val) => {
          setState((state) => ({ ...state, date: val }));
        }}
        value={date}
        sx={{ maxWidth: 200 }}
        disabled={disabled}
        shouldDisableDate={(day) => {
          return calUtil.isWeekend(day.$d) || calUtil.isHoliday(day.$d);
        }}
      />
      {!disabled && (
        <Button onClick={() => setShowYearDialog(true)} sx={{ mt: 2 }}>
          {t("calendar.year")}
        </Button>
      )}
    </Box>
  );
});

function initialCalendarState() {
  return {
    date: f.dateInput(new Date()),
    editEvent: null,
    showYearDialog: false,
  };
}

export default function Calendar() {
  const currentRoles = useRoles();

  const loaderData = useLoaderData();
  const location = loaderData.data.location;
  const buffers = location.buffers;
  const resources = loaderData.data.calendars.data;
  const [state, setState] = React.useState(initialCalendarState);
  const location_id = useCurrentLocation();

  const { date, editEvent, showYearDialog } = state;
  const setEditEvent = React.useCallback(
    (editEvent) => setState((state) => ({ ...state, editEvent })),
    [setState],
  );
  const setShowYearDialog = React.useCallback(
    (showYearDialog) => setState((state) => ({ ...state, showYearDialog })),
    [setState],
  );

  return (
    <>
      <Toolbar
        date={date}
        setState={setState}
        disabled={
          !(
            currentRoles.has(role.Manager_Customer_Care) ||
            currentRoles.has(role.Read_Only_Full)
          )
        }
      />
      <Cal
        location={location}
        location_id={location_id}
        date={date}
        setEditEvent={setEditEvent}
        resources={resources}
        buffers={buffers}
      />
      {editEvent && (
        <AppointmentDetailDialog
          appointmentList={editEvent}
          handleCloseAppointmentDetailDialog={() => setEditEvent(null)}
          openAppointmentDetailDialog={!!setEditEvent}
        />
      )}

      {showYearDialog && (
        <DatePicker
          close={() => setShowYearDialog(false)}
          onChange={(val) => {
            setState((state) => ({
              ...state,
              date: val,
              showYearDialog: false,
            }));
          }}
        />
      )}
    </>
  );
}
